import type {
  WorkflowCategory,
  WorkflowDefinition,
  WorkflowSchedule,
} from '../models/workflow.types'

export function humanizeWorkflowToken(value: string): string {
  return value
    .trim()
    .replaceAll(/[_-]+/gu, ' ')
    .replaceAll(/\b\p{L}/gu, (letter) => letter.toUpperCase())
}

export function workflowCategories(definitions: readonly WorkflowDefinition[]): WorkflowCategory[] {
  return [...new Set(definitions.map((item) => item.workflow_type).filter(Boolean))]
    .sort((left, right) => left.localeCompare(right))
    .map((field) => ({ field, label: humanizeWorkflowToken(field) }))
}

export function workflowPlatforms(definitions: readonly WorkflowDefinition[]): string[] {
  return [
    ...new Set(
      definitions.flatMap((definition) => definition.metadata?.data_sources ?? []).filter(Boolean),
    ),
  ].sort((left, right) => left.localeCompare(right))
}

export interface DefinitionFilter {
  query: string
  categories: readonly string[]
  platform: string | null
}

export function filterDefinitions(
  definitions: readonly WorkflowDefinition[],
  filter: DefinitionFilter,
): WorkflowDefinition[] {
  const query = filter.query.trim().toLocaleLowerCase()
  return definitions.filter((definition) => {
    const categoryMatches =
      filter.categories.length === 0 || filter.categories.includes(definition.workflow_type)
    const platformMatches =
      !filter.platform || (definition.metadata?.data_sources ?? []).includes(filter.platform)
    const queryMatches =
      !query ||
      definition.name.toLocaleLowerCase().includes(query) ||
      String(definition.id).toLocaleLowerCase().includes(query)
    return categoryMatches && platformMatches && queryMatches
  })
}

export interface ScheduleFilter {
  query: string
  status: 'all' | 'active' | 'paused'
  platform: string | null
}

export function filterSchedules(
  schedules: readonly WorkflowSchedule[],
  definitions: readonly WorkflowDefinition[],
  filter: ScheduleFilter,
): WorkflowSchedule[] {
  const query = filter.query.trim().toLocaleLowerCase()
  const definitionsById = new Map(definitions.map((item) => [String(item.id), item]))
  return schedules.filter((schedule) => {
    const definition = definitionsById.get(String(schedule.workflow_id))
    const statusMatches =
      filter.status === 'all' || (filter.status === 'active') === schedule.is_active
    const platformMatches =
      !filter.platform || (definition?.metadata?.data_sources ?? []).includes(filter.platform)
    const queryMatches =
      !query ||
      String(schedule.id).includes(query) ||
      definition?.name.toLocaleLowerCase().includes(query)
    return Boolean(statusMatches && platformMatches && queryMatches)
  })
}
